// StudyDost — Privacy Policy
// frontend/src/pages/PrivacyPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LAST_UPDATED = 'May 3, 2026';

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <StaticPage title="Privacy Policy" navigate={navigate}>
      <Meta>Last updated: {LAST_UPDATED}</Meta>

      <Section title="1. Introduction">
        <p>Let's Redefine Private Limited ("we", "us", or "our") operates StudyDost ("the Service") at studydost.ai. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Service.</p>
        <p>By using StudyDost, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use the Service.</p>
      </Section>

      <Section title="2. Information We Collect">
        <SubSection title="2.1 Information you provide directly">
          <ul>
            <li><strong>Phone number</strong> — used to identify your account and send OTPs for login.</li>
            <li><strong>Name</strong> — optional, used to personalise your experience.</li>
            <li><strong>Class, board, school name</strong> — used to provide curriculum-relevant answers.</li>
            <li><strong>Questions and photos</strong> — the text, images, and voice notes you send to StudyDost to get answers.</li>
          </ul>
        </SubSection>
        <SubSection title="2.2 Information collected automatically">
          <ul>
            <li><strong>Usage data</strong> — number of questions asked, subjects, timestamps, and interaction patterns.</li>
            <li><strong>Device and browser information</strong> — browser type, operating system, and IP address for security purposes.</li>
            <li><strong>WhatsApp interaction data</strong> — messages sent to our WhatsApp Business number, processed via the Meta WhatsApp Business API.</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="3. How We Use Your Information">
        <ul>
          <li>To provide, operate, and improve the StudyDost service</li>
          <li>To personalise answers based on your class, board, and language preference</li>
          <li>To send you OTPs for account login</li>
          <li>To generate weekly reports for parents who are linked to a student account</li>
          <li>To track study streaks, XP, and progress for gamification features</li>
          <li>To detect and prevent fraud, abuse, and security incidents</li>
          <li>To comply with legal obligations</li>
          <li>To improve our AI models and answer quality using anonymised, aggregated data</li>
        </ul>
      </Section>

      <Section title="4. AI and Data Processing">
        <p>Your questions are processed by OpenAI's GPT-4o and Whisper models to generate answers. This means your question content is sent to OpenAI's servers for processing. OpenAI's data usage policies apply. We do not use your questions to train OpenAI's models.</p>
        <p>We may use anonymised question-answer pairs internally to improve StudyDost's answer quality and to fine-tune our own models in future. Personally identifiable information is removed before any such use.</p>
      </Section>

      <Section title="5. Data Sharing and Disclosure">
        <p>We do not sell your personal data. We may share your information only in these circumstances:</p>
        <ul>
          <li><strong>Service providers</strong> — OpenAI (AI processing), Supabase (database), Railway (hosting), Razorpay (payments), Meta (WhatsApp delivery). Each is bound by their own privacy policies.</li>
          <li><strong>Teachers and schools</strong> — if you enter a teacher's referral code, your name and study statistics (questions asked, subjects, streak) may be visible to that teacher on their dashboard.</li>
          <li><strong>Parents</strong> — if you share your family code with a parent, they can see your weekly study activity report.</li>
          <li><strong>Legal requirements</strong> — if required by law, court order, or government authority in India.</li>
        </ul>
      </Section>

      <Section title="6. Data Retention">
        <p>We retain your account data for as long as your account is active. Question history is retained for up to 2 years for paid users and 90 days for free users, after which it is anonymised or deleted. You may request deletion of your account and associated data at any time by contacting us at privacy@studydost.ai.</p>
      </Section>

      <Section title="7. Children's Privacy">
        <p>StudyDost is designed for students of all ages including minors. We take children's privacy seriously. We do not knowingly collect sensitive personal information from children beyond what is necessary to provide the educational service (phone number, class, school name).</p>
        <p>Parents or guardians may contact us to review, update, or delete their child's information. If you believe a child has provided us with information without parental consent, please contact us immediately at privacy@studydost.ai.</p>
      </Section>

      <Section title="8. Security">
        <p>We implement industry-standard security measures including HTTPS encryption, hashed tokens, rate limiting, and access controls. However, no method of transmission over the internet is 100% secure. We encourage you to use a strong OTP and not share your account access with others.</p>
      </Section>

      <Section title="9. Your Rights">
        <p>Under applicable Indian law (including the Digital Personal Data Protection Act, 2023), you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate personal data</li>
          <li>Request erasure of your personal data</li>
          <li>Withdraw consent for data processing (this will result in account deletion)</li>
          <li>Nominate a representative to exercise these rights on your behalf</li>
        </ul>
        <p>To exercise any of these rights, email us at privacy@studydost.ai with your registered phone number.</p>
      </Section>

      <Section title="10. Cookies">
        <p>Our web app uses essential cookies and browser local storage to maintain your login session. We do not use advertising or tracking cookies. We use Google Analytics to understand aggregate usage patterns — this data is anonymised and does not identify individual users.</p>
      </Section>

      <Section title="11. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page with an updated date. Continued use of the Service after changes constitutes acceptance of the updated policy.</p>
      </Section>

      <Section title="12. Contact">
        <p>For any privacy-related questions or requests:</p>
        <p><strong>Let's Redefine Private Limited</strong><br />
        Email: hello@studydost.ai<br />
        Website: studydost.ai<br />
        Patna, Bihar, India</p>
      </Section>
    </StaticPage>
  );
}

function StaticPage({ title, navigate, children }) {
  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.back} onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <span style={s.navTitle}>{title}</span>
        <div style={{ width: 36 }} />
      </div>
      <div style={s.content}>
        <h1 style={s.pageTitle}>{title}</h1>
        {children}
        <FooterLinks navigate={navigate} />
      </div>
    </div>
  );
}

function Meta({ children }) {
  return <p style={s.meta}>{children}</p>;
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>{title}</h2>
      <div style={s.body}>{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={s.subSection}>
      <h3 style={s.subTitle}>{title}</h3>
      {children}
    </div>
  );
}

function FooterLinks({ navigate }) {
  return (
    <div style={s.footer}>
      <p style={s.footerText}>© {new Date().getFullYear()} Let's Redefine Private Limited</p>
      <div style={s.footerLinks}>
        <button style={s.footerLink} onClick={() => navigate('/about')}>About</button>
        <span style={s.dot}>·</span>
        <button style={s.footerLink} onClick={() => navigate('/terms')}>Terms</button>
        <span style={s.dot}>·</span>
        <button style={s.footerLink} onClick={() => navigate('/refund')}>Refund Policy</button>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif", paddingBottom: 60 },
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#FFF', borderBottom: '1px solid #E8EEFF', position: 'sticky', top: 0, zIndex: 10 },
  back: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4, color: '#1E40AF' },
  navTitle: { fontSize: 17, fontWeight: 700, color: '#1E40AF' },
  content: { maxWidth: 720, margin: '0 auto', padding: '32px 20px' },
  pageTitle: { fontSize: 28, fontWeight: 800, color: '#0F172A', marginBottom: 4, letterSpacing: '-0.02em' },
  meta: { fontSize: 13, color: '#94A3B8', marginBottom: 32, marginTop: 4 },
  section: { marginBottom: 32 },
  sectionTitle: { fontSize: 17, fontWeight: 700, color: '#1E40AF', marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid #EFF6FF' },
  subSection: { marginTop: 12, marginBottom: 8 },
  subTitle: { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 },
  body: { fontSize: 14, color: '#374151', lineHeight: 1.8, display: 'flex', flexDirection: 'column', gap: 10 },
  footer: { marginTop: 48, paddingTop: 24, borderTop: '1px solid #E8EEFF', textAlign: 'center' },
  footerText: { fontSize: 13, color: '#94A3B8', marginBottom: 8 },
  footerLinks: { display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
  footerLink: { background: 'none', border: 'none', color: '#4F46E5', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  dot: { color: '#CBD5E1' },
};

export { StaticPage, Section, SubSection, Meta, FooterLinks, s as staticStyles };
